var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { StatusIconType } from '../../../shared';
import { ScheduledShiftStatusType, useScheduledShiftStatus } from '../../../../hooks';
import { gql } from '@apollo/client';
import { TimeOffEntryStatus } from '@amzn/atoz-shiftapp-types';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { formatDuration, getDurationStr, getTimeIntervalStr, useAssociateContext } from '@amzn/atoz-shiftapp-utilities';
export var useShiftCardScheduledStatusData = function (_a) {
    var _b;
    var dropOpportunity = _a.dropOpportunity, scheduleShift = _a.scheduleShift, shiftsForDay = _a.shiftsForDay, swaps = _a.swaps, swapRequests = _a.swapRequests, siteSwapConfiguration = _a.siteSwapConfiguration, swapRequestButtonIsVisible = _a.swapRequestButtonIsVisible, timeoffEntries = _a.timeoffEntries, _c = _a.vtoOpportunities, vtoOpportunities = _c === void 0 ? [] : _c;
    var t = useTranslation().t;
    var pantherVtoDisabled = useAssociateContext().features.pantherVtoDisabled;
    var shiftStatusMap = useScheduledShiftStatus({
        dropOpportunity: dropOpportunity,
        hideDropCountDown: true,
        scheduleShift: scheduleShift,
        swaps: swaps,
        swapRequests: swapRequests,
        siteSwapConfiguration: siteSwapConfiguration,
        swapRequestButtonIsVisible: swapRequestButtonIsVisible,
        timeoffEntries: timeoffEntries,
        vtoOpportunities: vtoOpportunities
    });
    var statusToRender = [];
    if (shiftStatusMap[ScheduledShiftStatusType.vtoAvailable]) {
        vtoOpportunities === null || vtoOpportunities === void 0 ? void 0 : vtoOpportunities.map(function (vtoOpportunity, i) {
            var intervalStr = getTimeIntervalStr(DateTime.fromISO(vtoOpportunity.shift.timeRange.start), DateTime.fromISO(vtoOpportunity.shift.timeRange.end));
            var durationStr = getDurationStr(vtoOpportunity.shift.duration);
            statusToRender.push({
                dataTestId: "ShiftCardScheduledStatus_vtoAvailable_".concat(i),
                text: t('shift_management_features_ShiftCard_vtoAvailable', {
                    intervalStr: intervalStr,
                    durationStr: durationStr
                }),
                statusIcon: {
                    iconType: StatusIconType.IconInformationFill,
                    color: 'neutral70'
                }
            });
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasPendingSwaps]) {
        var hasMultipleShifts = shiftsForDay.length > 1;
        var pendingStatusText = hasMultipleShifts
            ? t('shift_management_features_ShiftCard_hasPendingSwaps_multipleShift')
            : t('shift_management_features_ShiftCard_hasPendingSwaps');
        statusToRender.push({
            dataTestId: "ShiftCardScheduledStatus_hasPendingSwaps",
            text: pendingStatusText,
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            },
            alternativeTitle: t('shift_management_features_ShiftCard_IconInformation_Alternative')
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasPendingSwapRequests]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasPendingSwapRequests',
            text: t('shift_management_features_ShiftCard_hasPendingSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            },
            alternativeTitle: t('shift_management_features_ShiftCard_IconInformation_Alternative')
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasDropPenalty]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasDropPenalty',
            text: t('shift_management_features_ShiftCard_hasDropPenalty'),
            statusIcon: {
                iconType: StatusIconType.IconAlertTriangleFill,
                color: 'yellow70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasTimeOff]) {
        timeoffEntries.map(function (timeOffEntry, i) {
            var intervalStr = getTimeIntervalStr(DateTime.fromISO(timeOffEntry.startAt), DateTime.fromISO(timeOffEntry.endAt));
            var isPending = timeOffEntry.status === TimeOffEntryStatus.Pending;
            statusToRender.push({
                dataTestId: "ShiftCardScheduledStatus_hasTimeOff_".concat(isPending ? 'pending' : 'approved', "_").concat(i),
                text: t(isPending
                    ? 'shift_management_features_ShiftCard_timeOffPending'
                    : 'shift_management_features_ShiftCard_timeOffApproved', { intervalStr: intervalStr }),
                statusIcon: {
                    iconType: isPending
                        ? StatusIconType.IconInformationFill
                        : StatusIconType.IconCheckCircleFill,
                    color: isPending ? 'neutral50' : 'green70'
                }
            });
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.vtoAccepted] &&
        !pantherVtoDisabled) {
        (_b = scheduleShift.acceptedVtoTimeRanges) === null || _b === void 0 ? void 0 : _b.map(function (vtoTimeRange, i) {
            var intervalStr = getTimeIntervalStr(DateTime.fromISO(vtoTimeRange.start), DateTime.fromISO(vtoTimeRange.end));
            var duration = DateTime.fromISO(vtoTimeRange.end).diff(DateTime.fromISO(vtoTimeRange.start), ['hours', 'minutes', 'seconds']);
            var durationStr = formatDuration(duration);
            statusToRender.push({
                dataTestId: "ShiftCardScheduledStatus_VtoAccepted_".concat(i),
                text: t('shift_management_features_ShiftCard_vtoAccepted', {
                    intervalStr: intervalStr,
                    durationStr: durationStr
                }),
                statusIcon: {
                    iconType: StatusIconType.IconCheckCircleFill,
                    color: 'green70'
                }
            });
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasCompletedSwaps]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasCompletedSwaps',
            text: t('shift_management_features_ShiftCard_hasCompletedSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasCompletedSwapRequests]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasCompletedSwapRequests',
            text: t('shift_management_features_ShiftCard_hasCompletedSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasExpiredSwaps]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasExpiredSwaps',
            text: t('shift_management_features_ShiftCard_hasExpiredSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconAlertTriangleFill,
                color: 'red70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasExpiredSwapRequests]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasExpiredSwapRequests',
            text: t('shift_management_features_ShiftCard_hasExpiredSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconAlertTriangleFill,
                color: 'red70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.hasProcessingSwapRequests]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_hasProcessingSwapRequests',
            text: t('shift_management_features_ShiftCard_hasProcessingSwaps'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            },
            alternativeTitle: t('shift_management_features_ShiftCard_IconInformation_Alternative')
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.noSwapsAvailable]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_noSwapsAvailable',
            text: t('shift_management_features_ShiftCard_noSwapsAvailableText'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.noSwapRequestsAvailable]) {
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_noSwapRequestsAvailable',
            text: t('shift_management_features_ShiftCard_noSwapsAvailableText'),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            }
        });
    }
    if (shiftStatusMap[ScheduledShiftStatusType.locksBeforeStart] &&
        (dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.addDeadline)) {
        var dropDeadline = DateTime.fromISO(dropOpportunity.addDeadline);
        statusToRender.push({
            dataTestId: 'ShiftCardScheduledStatus_locksBeforeStart',
            text: t('shift_management_features_OpportunityCard_locksBeforeShiftStart', {
                timeStr: dropDeadline.toLocaleString({
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            }),
            statusIcon: {
                iconType: StatusIconType.IconInformationFill,
                color: 'neutral70'
            }
        });
    }
    return statusToRender;
};
useShiftCardScheduledStatusData.fragments = {
    shift: gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_Shift on Shift {\n            id\n            duration {\n                value\n            }\n            timeRange {\n                start\n                end\n            }\n        }\n    "], ["\n        fragment UseShiftCardScheduledStatusData_Shift on Shift {\n            id\n            duration {\n                value\n            }\n            timeRange {\n                start\n                end\n            }\n        }\n    "]))),
    scheduleShift: gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_ScheduleShift on ScheduleShift {\n            acceptedVtoTimeRanges {\n                start\n                end\n            }\n            ...UseScheduledShiftStatus_ScheduleShift\n        }\n\n        ", "\n    "], ["\n        fragment UseShiftCardScheduledStatusData_ScheduleShift on ScheduleShift {\n            acceptedVtoTimeRanges {\n                start\n                end\n            }\n            ...UseScheduledShiftStatus_ScheduleShift\n        }\n\n        ", "\n    "])), useScheduledShiftStatus.fragments.scheduleShift),
    shiftOpportunity: gql(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_ShiftOpportunity on ShiftOpportunity {\n            shift {\n                ...UseShiftCardScheduledStatusData_Shift\n            }\n            ...UseScheduledShiftStatus_ShiftOpportunity\n        }\n\n        ", "\n    "], ["\n        fragment UseShiftCardScheduledStatusData_ShiftOpportunity on ShiftOpportunity {\n            shift {\n                ...UseShiftCardScheduledStatusData_Shift\n            }\n            ...UseScheduledShiftStatus_ShiftOpportunity\n        }\n\n        ", "\n    "])), useScheduledShiftStatus.fragments.shiftOpportunity),
    swaps: gql(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_Swaps on Swaps {\n            ...UseScheduledShiftStatus_Swaps\n        }\n\n        ", "\n    "], ["\n        fragment UseShiftCardScheduledStatusData_Swaps on Swaps {\n            ...UseScheduledShiftStatus_Swaps\n        }\n\n        ", "\n    "])), useScheduledShiftStatus.fragments.swaps),
    siteSwapConfiguration: gql(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_SiteSwapConfiguration on SiteSwapConfiguration {\n            ...UseScheduledShiftStatus_SiteSwapConfiguration\n        }\n\n        ", "\n    "], ["\n        fragment UseShiftCardScheduledStatusData_SiteSwapConfiguration on SiteSwapConfiguration {\n            ...UseScheduledShiftStatus_SiteSwapConfiguration\n        }\n\n        ", "\n    "])), useScheduledShiftStatus.fragments.siteSwapConfiguration),
    swapRequests: gql(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n        fragment UseShiftCardScheduledStatusData_SwapRequests on SwapRequests {\n            ...UseScheduledShiftStatus_SwapRequests\n        }\n\n        ", "\n    "], ["\n        fragment UseShiftCardScheduledStatusData_SwapRequests on SwapRequests {\n            ...UseScheduledShiftStatus_SwapRequests\n        }\n\n        ", "\n    "])), useScheduledShiftStatus.fragments.swapRequests)
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
